import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  CardContent,
  Grid,
  Theme,
  Typography,
  Tab,
  Button,
  Tooltip,
  BoxProps,
  Box,
  styled,
  alpha,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { Form, Formik, FormikProps, FormikHelpers, useFormikContext } from "formik";
import * as _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
  IVacationBidPackage,
  IVacationBidPackageResponse,
  IVacationBidSettingsValue,
  IVacationBidSlot,
  IVacationNfaBuild,
  VacationBidRound,
} from "@vmosolutions/oc-interfaces";

// Components
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { OcTabs } from "src/components/OcTabs/OcTabs";
import {
  FormikTextField,
  FormikCheckboxField,
  FormikDateTimeField,
} from "src/components/FormikComponents/FormikWrappers";
import { CrewBaseSelectField } from "src/components/Custom/Fields/CrewBaseSelectField";
import { VacationCrewGroupsField } from "./CrewInPackage/VacationCrewGroupsField";
import { VacationSlotsTab } from "./Slots/VacationSlotsTab";
import { ViewBidsTab } from "./Awarding/ViewBidsTab";
import { AwardBidsTab } from "./Awarding/AwardBidsTab";
import { AwardingConfirmDialog, IAwardingConfirm } from "./Awarding/AwardingConfirmDialog";
import { useVacationAwarding } from "./Awarding/useVacationAwarding";
import {
  awardableRounds,
  defaultAwardingRound,
  defaultViewRound,
  nextRoundChange,
  publishBlockedReason,
  publishSummaryLines,
} from "./Awarding/awardingHelpers";
import { ManageAwardsTab } from "./ChangeRequests/ManageAwardsTab";
import { ImportAwardsTab } from "./AwardImport/ImportAwardsTab";
import { useBaseTimezone } from "./ChangeRequests/useBaseTimezone";
import { useChangeRequestHistory } from "./ChangeRequests/useChangeRequestHistory";
import { NfaBuildResultsDialog } from "./NfaBuild/NfaBuildResultsDialog";
import { useVacationNfaBuild } from "./NfaBuild/useVacationNfaBuild";
import {
  isNfaBuildStopped,
  nfaBuildButtonLabel,
  nfaBuildConfirmLines,
  nfaBuildFinishedNotice,
} from "./NfaBuild/nfaBuildHelpers";

// Redux
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { updateTabNodeByWindowIdAndProps } from "src/redux/actions/Mosaic.Actions";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { INotifications } from "src/API/AmplifyAPIWrapper";
import { getFormFieldDiff } from "src/utils/index";

import { IVacationBidPackageEditorWindowProps, EDITOR_TAB, WINDOW_TITLE } from "./types";
import { VACATION_BID_TEST_ID } from "./testIds";
import {
  createVacationBidPackage,
  emptyVacationBidPackage,
  getVacationBidPackage,
  lastRound,
  lockedRoundFields,
  MAX_TIMER_MS,
  packageRounds,
  roundBiddingHasClosed,
  slotsAreProtected,
  updateVacationBidPackage,
} from "./helpers";
import { VacationBidPackageSchema } from "./vacationBidPackageSchema";

const VacationBidPackageEditorStyles = (theme: Theme): any => ({
  root: {
    display: "flex",
    width: "100%",
    height: "100%",
    position: "relative",
    "& .content_pane": {
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
      height: "100%",

      "& .MuiAccordion-root.vacation_bid_package_editor": {
        margin: 0,
        height: "100%",
        backgroundColor: theme.custom.timeline.contextListBackground,
        "& .MuiAccordionSummary-root": {
          minHeight: "48px",
          cursor: "default",
          "& .MuiAccordionSummary-content.tab_header": {
            fontWeight: 700,
            margin: 0,
            minHeight: "48px",
            alignItems: "center",
            "& .MuiTabs-root": {
              height: "24px",
              margin: "0px 10px",
              // OcTabs dims every tab to 0.7; an available step has to read as available, so only
              // the steps that cannot be opened yet are dimmed.
              "& button.MuiTab-root": {
                opacity: 1,
                color: theme.palette.text.primary,
                "&.Mui-disabled": {
                  opacity: 0.4,
                  fontStyle: "italic",
                  color: theme.custom.forms.labels,
                },
              },
            },
          },
        },
        "& .MuiCollapse-root": {
          height: "calc(100% - 50px) !important",
          "& .MuiCollapse-wrapper": {
            margin: "0 15px",
            backgroundColor: theme.palette.background.paper,
            // MUI sizes the wrapper at 100% of the collapse; the margins are on top of that, so
            // the width has to give them back or the content runs past the right edge.
            width: "calc(100% - 30px)",
            overflowX: "auto",
            height: "100%",

            "& .MuiCollapse-wrapperInner": {
              height: "100%",
              "& div[role=region]": {
                height: "100%",
                "& .MuiAccordionDetails-root": {
                  height: "100%",
                  width: "100%",
                  // The tabs are laid out for at least this width; narrower, they scroll sideways while the
                  // header above still shrinks, so its title and actions stay in view.
                  minWidth: "914px",
                  position: "relative",
                  "& .loadingSpinner": {
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 10,
                  },
                  alignItems: "flex-start",
                  ...theme.typography.body2,
                  "&.tab_0, &.tab_1, &.tab_2, &.tab_3, &.tab_4, &.tab_5": {
                    overflowY: "auto",
                  },
                  "&.tab_1, &.tab_2, &.tab_3, &.tab_4, &.tab_5": {
                    overflowX: "hidden",
                    padding: 0,
                    backgroundColor: theme.custom.timeline.contextListBackground,
                  },
                  "& .tab_content": {
                    "& p.required_fields_label": {
                      margin: 0,
                      fontWeight: 300,
                      fontSize: 10,
                      textTransform: "uppercase",
                      color: theme.custom.forms.labels,
                    },
                    "& .required_fields": {
                      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
                      margin: "2px 0px",
                      padding: "10px",
                    },
                    "& .optional_fields": {
                      margin: "2px 0px",
                      padding: "10px",
                    },
                    "& .time_zone_note": {
                      width: "100%",
                      margin: "4px 0px 0px 5px",
                      color: theme.custom.forms.labels,
                    },
                    // Rows size themselves to their fields. A fixed width here went stale the
                    // moment the fields gained a timezone toggle: the pair no longer fitted, wrapped,
                    // and space-between pushed the first one to the far edge.
                    "& .datetime_row": {
                      width: "100%",
                      flexWrap: "wrap",
                      columnGap: "16px",
                      rowGap: "4px",
                      alignItems: "flex-start",
                    },
                    "& .bid_window_color_swatch": {
                      margin: "5px",
                      height: "24px",
                      width: "5px",
                      backgroundColor: "purple",
                    },
                    "& .second_window_color_swatch": {
                      margin: "5px",
                      height: "24px",
                      width: "5px",
                      backgroundColor: "darkorange",
                    },
                    "& .bid_block_color_swatch": {
                      margin: "5px",
                      height: "24px",
                      width: "5px",
                      backgroundColor: "teal",
                    },
                    "& .field": {
                      width: "auto",
                      minWidth: "238px",
                      "&.textfield": {
                        "&.title": {
                          "& .input_control": {
                            "& label": { width: "auto" },
                            "& .input-base": { width: "250px" },
                          },
                        },
                        "&.max_awards": {
                          "& .input_control": {
                            "& label": { width: "auto" },
                            "& .input-base": { width: "80px" },
                          },
                        },
                      },
                      "&.selectfield": {
                        "&.base_airport": {
                          width: "320px",
                          minWidth: "320px",
                          "& label": { width: "auto" },
                        },
                        "&.crewgroups": {
                          width: "calc(100% - 200px)",
                          minWidth: "calc(100% - 200px)",
                          "& label": { width: "auto" },
                        },
                      },
                      "&.datetimefield": {
                        "& label": { width: "130px" },
                        // Wide enough for the empty field's "MMM DD, YYYY" placeholder.
                        "& .timezone_tooltip .input-base": { width: "120px", "& input": { width: "87px" } },
                      },
                      "&.checkboxfield": {
                        minWidth: "120px",
                      },
                    },

                    "& .grid_item_divider": {
                      width: "100%",
                      height: "1px",
                      border: "none",
                      backgroundColor: theme.custom.forms.labels,
                      marginBlockStart: "1em",
                      marginBlockEnd: "1em",
                      marginInlineStart: "1em",
                      marginInlineEnd: "1em",
                    },
                  },
                },
              },
            },
          },
        },
        "& .force_crew_rebuild": {
          width: "auto",
          "& .label": { marginLeft: "10px" },
        },
        "& .crew_on_bidpackage_table": {
          marginTop: "10px",
        },
      },

      "& .form_actions": {
        width: "auto",
        flexShrink: 0,
        marginLeft: "auto",
        marginRight: 0,
        "& .MuiButtonBase-root": {
          padding: "12px 7px",
          "&.reset,&.save,&.refresh,&.review,&.publish,&.build_nfas,&.nfa_build_results": {
            padding: "2px 8px",
            margin: "0px 5px",
            fontSize: 12,
          },
        },
      },

      "& .refresh_btn": { padding: "2px 8px", margin: "5px 5px", fontSize: 12, height: "24px" },
    },
  },
});

const TabStyles: React.FC<BoxProps> = styled(Box)(({ theme }) => ({
  height: "100%",
  // A flex item with no width shrink-to-fits, which left the editor narrower than its panel and
  // gave each tab a different width.
  width: "100%",
  minWidth: 0,
  "& .MuiCardContent-root": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 16,
    paddingBottom: "16px !important",

    "& .form_container": {
      height: "100%",
      "& form": {
        width: "100%",
      },
      "& .form_title": {
        padding: "4px 0px",
      },
    },
    "& .form_title": {
      margin: "0px 10px",
      color: theme.custom.forms.labels,
    },
  },
}));

export type IVacationBidPackageForm = FormikProps<IVacationBidPackage>;

/**
 * Passes the props MUI Tabs injects into a Tab through a wrapper element. Tooltips need a wrapper
 * around a disabled Tab because a disabled element never fires the mouse events they listen for.
 */
interface IClonePropsProps {
  children: (tabProps: Record<string, unknown>) => JSX.Element;
  [tabProp: string]: unknown;
}

function CloneProps(props: IClonePropsProps): JSX.Element {
  const { children, ...other } = props;
  return children(other);
}

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === "";

/**
 * Keeps the second round fields as a unit: when the second window open date is cleared, its close
 * date and the first-round award cap are cleared with it.
 */
export const SecondRoundSync = ({ locked }: { locked: Set<string> }): null => {
  const { values, setFieldValue } = useFormikContext<IVacationBidPackage>();
  const secondOpenIsEmpty = isEmptyValue(values.secondBidWindowOpen);
  const secondClose = values.secondBidWindowClose;
  const maxAwards = values.maxAwardsFirstRound;
  // A locked cap means round 1 is published and round 2 cannot be removed, so validation flags the cleared date instead.
  const capLocked = locked.has("maxAwardsFirstRound");

  useEffect(() => {
    if (!secondOpenIsEmpty || capLocked) return;
    if (secondClose !== null && secondClose !== undefined) void setFieldValue("secondBidWindowClose", null);
    if (maxAwards !== null && maxAwards !== undefined) void setFieldValue("maxAwardsFirstRound", null);
  }, [secondOpenIsEmpty, capLocked, secondClose, maxAwards, setFieldValue]);

  return null;
};

/** Drops an unsaved edit to a field the moment it locks, so the next update does not send a change the server refuses. */
export const LockedFieldsSync = ({ locked }: { locked: Set<string> }): null => {
  const { values, initialValues, setValues } = useFormikContext<IVacationBidPackage>();
  const lockedBefore = useRef<Set<string>>(locked);

  useEffect(() => {
    const newlyLocked = [...locked].filter(
      (field) => !lockedBefore.current.has(field)
    ) as (keyof IVacationBidPackage)[];
    lockedBefore.current = locked;
    // Only real edits: a package still loading in has none, and writing early would validate the form it replaces.
    const edited = newlyLocked.filter((field) => values[field] !== initialValues[field]);
    if (edited.length) {
      void setValues({ ...values, ...Object.fromEntries(edited.map((field) => [field, initialValues[field]])) });
    }
  }, [locked, values, initialValues, setValues]);

  return null;
};

/** Loads the base airport as it is picked, so the date fields can show and take its local time before a save. */
export const BaseAirportSync = (): null => {
  const { values } = useFormikContext<IVacationBidPackage>();
  useBaseTimezone(values.baseAirport ?? undefined);
  return null;
};

type UpdatePackage = (
  values: IVacationBidPackage,
  helpers: FormikHelpers<IVacationBidPackage>,
  initialValues: IVacationBidPackage
) => Promise<void>;

interface IDisableConfirm {
  confirm: IAwardingConfirm;
  /** Runs however the dialog closes. */
  onClose: () => void;
}

/** The confirm shown before an existing package is disabled. Closing it unconfirmed lets the form submit again. */
const disableWarning = (
  updatePackage: UpdatePackage,
  v: IVacationBidPackage,
  h: FormikHelpers<IVacationBidPackage>,
  initialFormValues: IVacationBidPackage
): IDisableConfirm => {
  let confirmed = false;
  return {
    confirm: {
      title: "Disable Vacation Bid Package?",
      lines: [
        "Are you certain you wish to disable this vacation bid package? Doing so will remove package availability from associated crew members.",
      ],
      confirmLabel: "Disable",
      onConfirm: () => {
        confirmed = true;
        void updatePackage(v, h, initialFormValues);
      },
    },
    onClose: () => {
      if (!confirmed) h?.setSubmitting(false);
    },
  };
};

/**
 * Vacation Bid Package Editor - creates and updates the packages crew bid their vacation blocks against.
 */
export const VacationBidPackageEditorWindow = (props: IVacationBidPackageEditorWindowProps): JSX.Element => {
  const { id, path, windowProps } = props;

  const dispatch = useDispatch();

  const vacationBiddingSetting: IVacationBidSettingsValue = useSelector(
    (state: IRootState) => state.permissions.vacationBidding
  );

  const [disableConfirm, setDisableConfirm] = useState<IDisableConfirm | null>(null);
  // A window opened straight onto a loaded package starts from it; otherwise from an empty package.
  const [initialFormValues, setInitialFormValues] = useState<IVacationBidPackage>(
    () => windowProps?.vacationBidPackage ?? emptyVacationBidPackage()
  );

  // The saved slot table. Held apart from the form because saving blocks must not reinitialize the
  // Details fields and discard edits the admin has not saved yet.
  const [savedSlots, setSavedSlots] = useState<IVacationBidSlot[]>(() => windowProps?.vacationBidPackage?.slots ?? []);

  // A package written back into the window props by a save reinitializes the form. Compared on the
  // way in because the mosaic tree is cloned on every window update, so identity alone means nothing.
  const [syncedWindowPackage, setSyncedWindowPackage] = useState<IVacationBidPackage | undefined>(
    windowProps?.vacationBidPackage
  );
  if (windowProps?.vacationBidPackage !== syncedWindowPackage) {
    setSyncedWindowPackage(windowProps?.vacationBidPackage);
    if (!!windowProps?.vacationBidPackage?._id && !_.isEqual(windowProps.vacationBidPackage, initialFormValues)) {
      setInitialFormValues(windowProps.vacationBidPackage);
      setSavedSlots(windowProps.vacationBidPackage.slots ?? []);
    }
  }

  // Incremented after every successful save so the crew tab refetches its rows.
  const [crewVersion, setCrewVersion] = useState<number>(0);

  // The rounds this package can be awarded on, and which of them the scheduler is working on. Their
  // choice holds while that round is still awardable; otherwise the earliest closed round still to publish.
  const [chosenRound, setChosenRound] = useState<VacationBidRound | null>(null);
  // Bidding closes on the clock, not on a render, so the rounds are reckoned as of a moment that is
  // moved on by the timer below. Without it a window left open across a close keeps the round it
  // started with until the editor is reopened.
  const [roundsAsOf, setRoundsAsOf] = useState<Date>(() => new Date());
  // An unbuilt package has nothing to award, whatever dates a duplicate's form carries over.
  const rounds = useMemo(
    () => (initialFormValues?._id ? awardableRounds(initialFormValues, roundsAsOf) : []),
    [initialFormValues, roundsAsOf]
  );

  useEffect(() => {
    const change = nextRoundChange(initialFormValues, roundsAsOf);
    if (!change) return undefined;
    // A second past it, as slack for a timer that fires early or a drifting clock. One further off than a timer can
    // hold is woken in stages and rescheduled.
    const wait = Math.min(Math.max(change.getTime() - Date.now(), 0) + 1000, MAX_TIMER_MS);
    const timer = setTimeout(() => setRoundsAsOf(new Date()), wait);
    return () => clearTimeout(timer);
  }, [initialFormValues, roundsAsOf]);
  const awardingRound =
    chosenRound && rounds.includes(chosenRound) ? chosenRound : defaultAwardingRound(rounds, initialFormValues);

  // The timer can add a round while the tab is open, and defaultAwardingRound would then move the
  // scheduler onto it mid-task - the view is cleared and pending changes dropped on a round change.
  // Pinning what they are already on leaves the new round to the chooser, which now appears for it.
  useEffect(() => {
    if (!chosenRound && rounds.length) setChosenRound(awardingRound);
  }, [chosenRound, rounds, awardingRound]);

  const awarding = useVacationAwarding(initialFormValues?._id, awardingRound);

  // View Bids picks its own round, open or closed. Until the scheduler picks one it follows the clock.
  const [chosenViewRound, setChosenViewRound] = useState<VacationBidRound | null>(null);
  // Rounds published in this editor, kept when the awarding round changes: the loaded package does not have them yet.
  const [publishedHere, setPublishedHere] = useState<VacationBidRound[]>([]);
  const viewRound =
    chosenViewRound && packageRounds(initialFormValues).includes(chosenViewRound)
      ? chosenViewRound
      : defaultViewRound(initialFormValues, roundsAsOf);
  const bidsView = useVacationAwarding(initialFormValues?._id, viewRound);
  const [publishConfirm, setPublishConfirm] = useState<IAwardingConfirm | null>(null);

  // A build runs in the background; when one this window watched comes to an end, say how it went.
  const handleNfaBuildFinished = useCallback(
    (build: IVacationNfaBuild): void => {
      const notice = nfaBuildFinishedNotice(build);
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: notice.variant === "success" ? 3000 : null,
          variant: notice.variant,
          message: notice.message,
        })
      );
    },
    [dispatch]
  );
  const nfaBuild = useVacationNfaBuild(initialFormValues?._id, handleNfaBuildFinished);
  const [nfaBuildConfirm, setNfaBuildConfirm] = useState<IAwardingConfirm | null>(null);
  const [nfaBuildDialogOpen, setNfaBuildDialogOpen] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Whether the last attempt to load the package came back empty.
  const [loadError, setLoadError] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(EDITOR_TAB.details);

  // Whether NFAs can be built is the server's call, so it is asked whenever Award Bids is shown,
  // and again for another package. `load` is remade per package.
  const loadNfaBuild = nfaBuild.load;
  useEffect(() => {
    if (tabValue === EDITOR_TAB.awards) void loadNfaBuild();
  }, [tabValue, loadNfaBuild]);

  /** Loads a package into the form and the slot table together. */
  const applyLoadedPackage = useCallback((pkg: IVacationBidPackage, duplicate?: boolean): void => {
    // A copy starts with an empty slot table, since slot ids are package-local, and with none of the original's
    // publications or change windows, which would lock its dates.
    const values = duplicate ? { ...pkg, _id: undefined, slots: [], awardPublications: [], changeWindows: [] } : pkg;
    setInitialFormValues(values);
    setSavedSlots(values.slots ?? []);
  }, []);

  // The last fetched id, so a duplicate - whose form has no _id - is not fetched again on every
  // window props change. Cleared when a load fails so the package can be fetched again.
  const fetchedListItemId = useRef<string | undefined>(undefined);

  const getPackage = useCallback(
    async (packageId: string, duplicate?: boolean): Promise<void> => {
      setIsLoading(true);
      // Cleared only by a load that returns a package, so a failure that throws locks the form too.
      setLoadError(true);
      try {
        const notifications: INotifications = {
          dispatch,
          errorMessage: "Unable to load the vacation bid package",
        };
        const response: IVacationBidPackageResponse | null = await getVacationBidPackage(packageId, notifications);
        if (response?.vacationBidPackage?._id) {
          applyLoadedPackage(response.vacationBidPackage, duplicate);
          setLoadError(false);
        } else {
          fetchedListItemId.current = undefined;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, applyLoadedPackage]
  );

  // Load the package named by the list item when it differs from the one in the form.
  useEffect(() => {
    const listItemId = windowProps?.vacationBidPackageListItem?._id;
    if (!!listItemId && listItemId !== initialFormValues?._id && listItemId !== fetchedListItemId.current) {
      fetchedListItemId.current = listItemId;
      void getPackage(listItemId, windowProps?.duplicate);
    }
  }, [windowProps, getPackage, initialFormValues?._id]);

  /**
   * True when the window was opened on a package that never loaded. Saving in this state would
   * write a package built from an empty form, so it stays locked until a retry succeeds.
   */
  const loadFailed = loadError && !isLoading;

  const handleRetryLoad = useCallback((): void => {
    const listItemId = windowProps?.vacationBidPackageListItem?._id;
    if (listItemId) {
      fetchedListItemId.current = listItemId;
      void getPackage(listItemId, windowProps?.duplicate);
    }
  }, [windowProps, getPackage]);

  // A package with no _id has not been built yet.
  const isNew = useMemo(() => !initialFormValues?._id, [initialFormValues]);

  // A copy arrives fully populated and so is never dirty, but saving it is the whole point of the
  // duplicate action. Stops applying once the save gives the window a package of its own.
  const isDuplicate = !!windowProps?.duplicate && !initialFormValues?._id;

  /**
   * Switches this window into update mode after a save, which also reinitializes the form.
   */
  const updateMosaicWindow = useCallback(
    (vacationBidPackage: IVacationBidPackage) => {
      dispatch(
        updateTabNodeByWindowIdAndProps(
          id,
          {
            title: vacationBidPackage.title || WINDOW_TITLE.editPackage,
            props: { vacationBidPackage },
          },
          windowProps,
          "VacationBidPackageEditorWindow"
        )
      );
    },
    [dispatch, id, windowProps]
  );

  const handleSelectTab = (event: React.SyntheticEvent, newValue: number): void => {
    event.preventDefault();
    event.stopPropagation();
    // Refreshed on the way in, so newly submitted bids and awards made on the other tab appear.
    if (newValue === EDITOR_TAB.bids && tabValue !== EDITOR_TAB.bids) void bidsView.load();
    if (newValue === EDITOR_TAB.awards && tabValue !== EDITOR_TAB.awards) void awarding.load();
    setTabValue(newValue);
  };

  const awardingOpen = rounds.length > 0;
  // Change windows need every round published; the tab says so until they are. Same clock as the awarding rounds.
  const manageOpen = !isNew && roundBiddingHasClosed(initialFormValues, lastRound(initialFormValues), roundsAsOf);
  const baseTimezone = useBaseTimezone(initialFormValues?.baseAirport);
  const changeHistory = useChangeRequestHistory(initialFormValues?._id, manageOpen && tabValue === EDITOR_TAB.bids);
  // The awarding round on screen counts as soon as its view reports it published, whoever published it.
  const viewPublished = !!awarding.view?.publication;
  const lockedFields = useMemo(
    () => lockedRoundFields(initialFormValues, [...publishedHere, ...(viewPublished ? [awardingRound] : [])]),
    [initialFormValues, publishedHere, viewPublished, awardingRound]
  );
  // These tabs save as they go, so the package's Reset and Update buttons do not apply to them.
  const tabSavesItself = tabValue === EDITOR_TAB.awards || tabValue === EDITOR_TAB.manage;
  const submittedBidCount = bidsView.view ? bidsView.view.crew.filter((z) => z.bid).length : null;

  // Only asked while the round can still be written: once it is published the names would be noise.
  const publishBlocked = awarding.view?.writesAllowed ? publishBlockedReason(awarding.view.crew) : null;

  const handlePublish = useCallback((): void => {
    if (!awarding.view) return;
    setPublishConfirm({
      title: `Publish Round ${awardingRound} Awards?`,
      lines: publishSummaryLines(awarding.view, awardingRound === lastRound(initialFormValues)),
      confirmLabel: "Publish",
      onConfirm: () => {
        // Publishing the last round is what lets NFAs be built, so the build status is asked again.
        void awarding.publish().then((published) => {
          if (!published) return;
          setPublishedHere((rounds) => [...rounds, awardingRound]);
          void nfaBuild.load();
        });
      },
    });
  }, [awarding, awardingRound, initialFormValues, nfaBuild]);

  const handleBuildNfas = useCallback((): void => {
    setNfaBuildConfirm({
      title: "Build NFAs?",
      lines: nfaBuildConfirmLines(nfaBuild.status?.build),
      confirmLabel: "Build NFAs",
      onConfirm: () => {
        setNfaBuildDialogOpen(true);
        void nfaBuild.start();
      },
    });
  }, [nfaBuild]);

  // Only the slot table is refreshed: re-propping the window here would reinitialize the form and
  // discard unsaved Details edits.
  const handleSlotsSaved = useCallback((pkg: IVacationBidPackage) => {
    setSavedSlots(pkg.slots ?? []);
  }, []);

  // Portal target for react-select menus so they escape the accordion's overflow.
  const mosaicPane = id ? document.getElementById(`${id}-panel`) : null;

  const handleCreateUpdatePackage = useCallback(
    async (
      values: IVacationBidPackage,
      helpers: FormikHelpers<IVacationBidPackage>,
      initialValues: IVacationBidPackage
    ): Promise<void> => {
      const { setSubmitting } = helpers;
      setSubmitting(true);

      // A new package sends everything; an existing one sends only the dirty fields.
      const payload: Partial<IVacationBidPackage> = isNew
        ? { ...values }
        : getFormFieldDiff({ ...values }, initialValues);

      // Slots persist through their own tab and never travel with the details.
      delete payload.slots;

      // Cleared second round fields are sent as null so the backend stores a single-round package.
      if ("secondBidWindowOpen" in payload && isEmptyValue(payload.secondBidWindowOpen)) {
        payload.secondBidWindowOpen = null;
      }
      if ("secondBidWindowClose" in payload && isEmptyValue(payload.secondBidWindowClose)) {
        payload.secondBidWindowClose = null;
      }
      if (
        "maxAwardsFirstRound" in payload &&
        (isEmptyValue(payload.maxAwardsFirstRound) || Number.isNaN(payload.maxAwardsFirstRound))
      ) {
        payload.maxAwardsFirstRound = null;
      }

      const notifications: INotifications = {
        dispatch,
        successMessage: `Vacation Bid Package ${isNew ? "Built" : "Updated"} Successfully`,
        errorMessage: `Unable to ${isNew ? "build" : "update"} vacation bid package`,
      };

      const response: IVacationBidPackageResponse | null =
        isNew || !values._id
          ? await createVacationBidPackage(payload, notifications)
          : await updateVacationBidPackage(values._id, payload, notifications);

      if (response?.vacationBidPackage?._id) {
        updateMosaicWindow(response.vacationBidPackage);
        setCrewVersion((version) => version + 1);
      }
      setSubmitting(false);
    },
    [dispatch, isNew, updateMosaicWindow]
  );

  return (
    <>
      <TabStyles>
        <CardContent sx={(theme) => VacationBidPackageEditorStyles(theme).root}>
          <Formik
            initialValues={initialFormValues}
            validationSchema={VacationBidPackageSchema}
            enableReinitialize={true}
            onSubmit={(v: IVacationBidPackage, h: FormikHelpers<IVacationBidPackage>) => {
              if (!!v?._id && v?.isDisabled) {
                // Disabling an existing package hides it from crew, so it is confirmed first.
                setDisableConfirm(disableWarning(handleCreateUpdatePackage, v, h, initialFormValues));
              } else {
                void handleCreateUpdatePackage(v, h, initialFormValues);
              }
            }}
          >
            {(packageForm: IVacationBidPackageForm) => {
              const { values, dirty, isValid, isSubmitting, handleReset } = packageForm;

              return (
                <Form style={{ height: "100%", width: "100%" }}>
                  <SecondRoundSync locked={lockedFields} />
                  <LockedFieldsSync locked={lockedFields} />
                  <BaseAirportSync />
                  <Grid container direction="row" wrap="nowrap" className="form_container">
                    <Grid className="content_pane">
                      <Accordion className="vacation_bid_package_editor" expanded={true} square={true}>
                        <AccordionSummary classes={{ content: "tab_header" }}>
                          {/* One row: the title and actions keep their width and the tabs scroll in what is left. */}
                          <Grid container wrap="nowrap" alignItems="center">
                            <Typography
                              variant="subtitle2"
                              style={{ fontSize: 16, marginRight: 10, whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                              Package Creation Steps
                            </Typography>
                            {!!values?.title && (
                              <Typography
                                variant="subtitle2"
                                className="package_title_heading"
                                sx={(theme) => ({
                                  fontSize: 14,
                                  fontWeight: 400,
                                  marginRight: "10px",
                                  color: theme.custom.forms.labels,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 320,
                                  minWidth: 80,
                                  flexShrink: 1,
                                })}
                              >
                                {values.title}
                              </Typography>
                            )}
                            {/* OcTabs ignores a passed sx, so this wrapper gives the tabs the leftover width. */}
                            <Box sx={{ flex: "1 1 0", minWidth: 0, display: "flex", alignItems: "center" }}>
                              <OcTabs
                                variant="scrollable"
                                orientation="horizontal"
                                scrollButtons="auto"
                                value={tabValue}
                                onChange={handleSelectTab}
                              >
                                <CloneProps>
                                  {(tabProps: any) => (
                                    <Tooltip title="Vacation bid package details" placement="top">
                                      <div>
                                        <Tab
                                          wrapped={true}
                                          {...tabProps}
                                          label="1) Details"
                                          classes={{ root: "form-tab" }}
                                          data-testid={VACATION_BID_TEST_ID.detailsTab}
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </CloneProps>
                                <CloneProps>
                                  {(tabProps: any) => (
                                    <Tooltip
                                      title={
                                        isNew
                                          ? "Build package to unlock addition of crew"
                                          : "Add crew by assigning desired groups"
                                      }
                                      placement="top"
                                    >
                                      <div>
                                        <Tab
                                          wrapped={true}
                                          {...tabProps}
                                          label="2) Add Crew"
                                          classes={{ root: "form-tab" }}
                                          disabled={isNew}
                                          data-testid={VACATION_BID_TEST_ID.crewTab}
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </CloneProps>
                                <CloneProps>
                                  {(tabProps: any) => (
                                    <Tooltip
                                      title={
                                        isNew
                                          ? "Build package to unlock vacation blocks"
                                          : "Add, generate or import the vacation blocks crew bid on"
                                      }
                                      placement="top"
                                    >
                                      <div>
                                        <Tab
                                          wrapped={true}
                                          {...tabProps}
                                          label="3) Vacation Blocks"
                                          classes={{ root: "form-tab" }}
                                          disabled={isNew}
                                          data-testid={VACATION_BID_TEST_ID.slotsTab}
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </CloneProps>
                                <CloneProps>
                                  {(tabProps: any) => (
                                    <Tooltip
                                      title={isNew ? "Build package to unlock bid viewing" : "See what crew have bid"}
                                      placement="top"
                                    >
                                      <div>
                                        <Tab
                                          wrapped={true}
                                          {...tabProps}
                                          label={
                                            submittedBidCount === null
                                              ? "4) View Bids"
                                              : `4) View Bids (${submittedBidCount})`
                                          }
                                          classes={{ root: "form-tab" }}
                                          disabled={isNew}
                                          data-testid={VACATION_BID_TEST_ID.bidsTab}
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </CloneProps>
                                <CloneProps>
                                  {(tabProps: any) => (
                                    <Tooltip
                                      title={
                                        isNew
                                          ? "Build package to unlock awarding"
                                          : !awardingOpen
                                            ? `Awarding opens once round ${awardingRound} bidding closes`
                                            : "Award bids in seniority order"
                                      }
                                      placement="top"
                                    >
                                      <div>
                                        <Tab
                                          wrapped={true}
                                          {...tabProps}
                                          label="5) Award Bids"
                                          classes={{ root: "form-tab" }}
                                          disabled={!awardingOpen}
                                          data-testid={VACATION_BID_TEST_ID.awardsTab}
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </CloneProps>
                                <CloneProps>
                                  {(tabProps: any) => (
                                    <Tooltip
                                      title={
                                        isNew
                                          ? "Build package to unlock award management"
                                          : !manageOpen
                                            ? "Awards can be managed once bidding closes"
                                            : "Open monthly change windows and decide crew requests"
                                      }
                                      placement="top"
                                    >
                                      <div>
                                        <Tab
                                          wrapped={true}
                                          {...tabProps}
                                          label="6) Manage Awards"
                                          classes={{ root: "form-tab" }}
                                          disabled={!manageOpen}
                                          data-testid={VACATION_BID_TEST_ID.manageTab}
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </CloneProps>
                                <CloneProps>
                                  {(tabProps: any) => (
                                    <Tooltip
                                      title={
                                        isNew
                                          ? "Build package to unlock importing awards"
                                          : "Import awards for a bid that was run outside Vmo"
                                      }
                                      placement="top"
                                    >
                                      <div>
                                        <Tab
                                          wrapped={true}
                                          {...tabProps}
                                          label="7) Import Awards"
                                          classes={{ root: "form-tab" }}
                                          disabled={isNew}
                                          data-testid={VACATION_BID_TEST_ID.importAwardsTab}
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </CloneProps>
                              </OcTabs>
                            </Box>
                            <Grid container wrap="nowrap" className="form_actions">
                              {tabValue === EDITOR_TAB.awards && !!nfaBuild.status?.build && (
                                <Button
                                  className="btn nfa_build_results"
                                  variant={isNfaBuildStopped(nfaBuild.status.build) ? "contained" : "outlined"}
                                  color={isNfaBuildStopped(nfaBuild.status.build) ? "error" : "secondary"}
                                  onClick={() => setNfaBuildDialogOpen(true)}
                                  data-testid={VACATION_BID_TEST_ID.nfaBuildResultsButton}
                                >
                                  {nfaBuildButtonLabel(nfaBuild.status.build)}
                                </Button>
                              )}
                              {tabValue === EDITOR_TAB.awards && (
                                <Tooltip title={nfaBuild.status?.buildRefusedReason ?? ""}>
                                  <span>
                                    <Button
                                      className="btn build_nfas"
                                      variant="contained"
                                      color="primary"
                                      disabled={!nfaBuild.status?.buildAllowed || nfaBuild.starting || awarding.saving}
                                      onClick={handleBuildNfas}
                                      data-testid={VACATION_BID_TEST_ID.buildNfasButton}
                                    >
                                      Build NFAs
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                              {tabValue === EDITOR_TAB.awards && (
                                <Tooltip title={publishBlocked ?? ""}>
                                  <span>
                                    <Button
                                      className="btn publish"
                                      variant="contained"
                                      color="secondary"
                                      disabled={
                                        !awarding.view?.writesAllowed ||
                                        awarding.saving ||
                                        awarding.loading ||
                                        !!publishBlocked
                                      }
                                      onClick={handlePublish}
                                      data-testid={VACATION_BID_TEST_ID.publishAwardsButton}
                                    >
                                      Publish Awards
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                              {!tabSavesItself && (
                                <Button
                                  className="btn reset"
                                  type="reset"
                                  variant="contained"
                                  color="secondary"
                                  disabled={(!dirty && isValid) || isSubmitting}
                                  onClick={handleReset}
                                  data-testid={VACATION_BID_TEST_ID.resetButton}
                                >
                                  Reset
                                </Button>
                              )}
                              {loadFailed && (
                                <Button
                                  className="btn retry_load"
                                  variant="outlined"
                                  color="secondary"
                                  onClick={handleRetryLoad}
                                  data-testid={VACATION_BID_TEST_ID.retryLoadButton}
                                >
                                  Retry Load
                                </Button>
                              )}
                              {!tabSavesItself && (
                                <Button
                                  className="btn save"
                                  variant="contained"
                                  color="primary"
                                  disabled={isSubmitting || !isValid || loadFailed || (!dirty && !isDuplicate)}
                                  type="submit"
                                  data-testid={VACATION_BID_TEST_ID.saveButton}
                                >
                                  {tabValue === EDITOR_TAB.details && isNew ? "Build Package" : "Update Package"}
                                </Button>
                              )}
                            </Grid>
                          </Grid>
                        </AccordionSummary>

                        <AccordionDetails classes={{ root: `tab_${tabValue}` }}>
                          <SpinnerProgress className="loadingSpinner" loading={isLoading} message="loading package" />
                          {/* DETAILS */}
                          <Grid
                            container
                            className="tab_content details"
                            style={{ display: tabValue === EDITOR_TAB.details ? "flex" : "none", overflow: "auto" }}
                          >
                            <Grid container>
                              <Typography className="required_fields_label">required fields</Typography>
                              <Grid container direction="row" className="required_fields">
                                <Grid container>
                                  <FormikTextField
                                    className="field textfield title"
                                    label="Title"
                                    name="title"
                                    inputProps={{ "data-testid": VACATION_BID_TEST_ID.title }}
                                  />
                                  <CrewBaseSelectField
                                    className="field selectfield base_airport"
                                    label="Base Airport"
                                    name="baseAirport"
                                    valueKey="ICAO"
                                    activeOnly={true}
                                    keepUnlistedValue={true}
                                    defaultValue={values.baseAirport}
                                    isClearable={true}
                                    placeholder="Select a crew base"
                                    mosaicPane={mosaicPane}
                                    data-testid={VACATION_BID_TEST_ID.baseAirport}
                                  />
                                  <FormikCheckboxField
                                    className="field checkboxfield isdisabled"
                                    label="Is Disabled"
                                    name="isDisabled"
                                    data-testid={VACATION_BID_TEST_ID.isDisabled}
                                  />
                                </Grid>
                                <Typography variant="caption" className="time_zone_note">
                                  {values.baseAirport
                                    ? `All times below are ${values.baseAirport} local time.`
                                    : "All times below are UTC. Pick a base airport to enter them in local time."}
                                  {lockedFields.has("secondBidWindowOpen")
                                    ? " Awards have been published, so the bid windows and rounds can no longer change."
                                    : lockedFields.size > 0 &&
                                      " Round 1 has been published, so its bid window and cap can no longer change, and round 2 can be re-dated but not removed."}
                                </Typography>
                                <hr className="grid_item_divider" />
                                <Grid container direction="column" alignItems="flex-start">
                                  <Grid container className="datetime_row">
                                    <div className="bid_window_color_swatch" />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Window Open"
                                      name="bidWindowOpen"
                                      disabled={lockedFields.has("bidWindowOpen")}
                                      showAirportTime={values.baseAirport}
                                      localizeDisplay={!!values.baseAirport}
                                      data-testid={VACATION_BID_TEST_ID.bidWindowOpen}
                                    />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Window Close"
                                      name="bidWindowClose"
                                      disabled={lockedFields.has("bidWindowClose")}
                                      showAirportTime={values.baseAirport}
                                      localizeDisplay={!!values.baseAirport}
                                      data-testid={VACATION_BID_TEST_ID.bidWindowClose}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Typography className="required_fields_label">optional</Typography>
                              <Grid container direction="row" className="optional_fields">
                                <Grid container direction="column" alignItems="flex-start">
                                  <Grid container className="datetime_row">
                                    <div className="second_window_color_swatch" />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="2nd Bid Window Open"
                                      name="secondBidWindowOpen"
                                      disabled={lockedFields.has("secondBidWindowOpen")}
                                      showAirportTime={values.baseAirport}
                                      localizeDisplay={!!values.baseAirport}
                                      data-testid={VACATION_BID_TEST_ID.secondBidWindowOpen}
                                    />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="2nd Bid Window Close"
                                      name="secondBidWindowClose"
                                      disabled={lockedFields.has("secondBidWindowClose")}
                                      showAirportTime={values.baseAirport}
                                      localizeDisplay={!!values.baseAirport}
                                      data-testid={VACATION_BID_TEST_ID.secondBidWindowClose}
                                    />
                                  </Grid>
                                  {!!values?.secondBidWindowOpen && (
                                    <Grid container className="datetime_row">
                                      <FormikTextField
                                        className="field textfield max_awards"
                                        label="Max Awards In Round 1"
                                        name="maxAwardsFirstRound"
                                        type="number"
                                        disabled={lockedFields.has("maxAwardsFirstRound")}
                                        inputProps={{ "data-testid": VACATION_BID_TEST_ID.maxAwardsFirstRound, min: 1 }}
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </Grid>

                              <Typography className="required_fields_label">required fields</Typography>
                              <Grid container direction="row" className="required_fields">
                                <Grid container direction="column" alignItems="flex-start">
                                  <Grid container className="datetime_row">
                                    <div className="bid_block_color_swatch" />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Block Start"
                                      name="bidBlockStart"
                                      showAirportTime={values.baseAirport}
                                      localizeDisplay={!!values.baseAirport}
                                      data-testid={VACATION_BID_TEST_ID.bidBlockStart}
                                    />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Block End"
                                      name="bidBlockEnd"
                                      showAirportTime={values.baseAirport}
                                      localizeDisplay={!!values.baseAirport}
                                      data-testid={VACATION_BID_TEST_ID.bidBlockEnd}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          {/* ADD CREW - mounted only while active so the tab fetches on entry */}
                          {tabValue === EDITOR_TAB.crew && (
                            <Grid
                              container
                              direction="column"
                              wrap="nowrap"
                              className="tab_content add_crew"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <VacationCrewGroupsField
                                id={id}
                                path={path}
                                mosaicPane={mosaicPane}
                                packageId={values._id}
                                packageTitle={values.title}
                                crewGroups={values.crewGroups ?? []}
                                initialCrewGroups={initialFormValues.crewGroups ?? []}
                                crewVersion={crewVersion}
                              />
                            </Grid>
                          )}

                          {/* VACATION BLOCKS - mounted only while active so its working copy resets on entry */}
                          {tabValue === EDITOR_TAB.slots && (
                            <Grid
                              container
                              direction="column"
                              wrap="nowrap"
                              className="tab_content vacation_blocks"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <VacationSlotsTab
                                id={id}
                                path={path}
                                mosaicPane={mosaicPane}
                                packageId={initialFormValues._id}
                                packageTitle={values.title}
                                slots={savedSlots}
                                bidBlockStart={initialFormValues.bidBlockStart}
                                bidBlockEnd={initialFormValues.bidBlockEnd}
                                slotsProtected={slotsAreProtected(initialFormValues)}
                                showSlotIdYear={vacationBiddingSetting?.showSlotIdYear ?? true}
                                onSlotsSaved={handleSlotsSaved}
                              />
                            </Grid>
                          )}

                          {/* VIEW BIDS */}
                          {tabValue === EDITOR_TAB.bids && (
                            <Grid
                              container
                              direction="column"
                              wrap="nowrap"
                              className="tab_content view_bids"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <ViewBidsTab
                                view={bidsView.view}
                                loading={bidsView.loading}
                                round={viewRound}
                                rounds={packageRounds(initialFormValues)}
                                onRoundChange={setChosenViewRound}
                                showSlotIdYear={vacationBiddingSetting?.showSlotIdYear ?? true}
                                onRefresh={() => {
                                  void bidsView.load();
                                  changeHistory.reload();
                                }}
                                changeRequests={changeHistory.requests}
                                timezoneId={baseTimezone}
                                onDismissWarning={bidsView.dismissWarning}
                              />
                            </Grid>
                          )}

                          {/* MANAGE AWARDS - mounted only while active so the tab reloads on entry */}
                          {tabValue === EDITOR_TAB.manage && (
                            <Grid
                              container
                              direction="column"
                              wrap="nowrap"
                              className="tab_content manage_awards"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <ManageAwardsTab
                                pkg={initialFormValues}
                                showSlotIdYear={vacationBiddingSetting?.showSlotIdYear ?? true}
                                changeWindowSchedule={vacationBiddingSetting?.changeWindowSchedule}
                              />
                            </Grid>
                          )}

                          {/* AWARD BIDS */}
                          {tabValue === EDITOR_TAB.awards && (
                            <Grid
                              container
                              direction="column"
                              wrap="nowrap"
                              className="tab_content award_bids"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <AwardBidsTab
                                awarding={awarding}
                                pkg={initialFormValues}
                                round={awardingRound}
                                rounds={rounds}
                                onRoundChange={setChosenRound}
                                showSlotIdYear={vacationBiddingSetting?.showSlotIdYear ?? true}
                              />
                            </Grid>
                          )}

                          {/* IMPORT AWARDS - mounted only while active, so a reopened tab starts clean */}
                          {tabValue === EDITOR_TAB.importAwards && (
                            <Grid
                              container
                              direction="column"
                              wrap="nowrap"
                              className="tab_content import_awards"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <ImportAwardsTab
                                packageId={initialFormValues?._id}
                                onImported={() => void awarding.load()}
                              />
                            </Grid>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </TabStyles>
      <AwardingConfirmDialog confirm={publishConfirm} onClose={() => setPublishConfirm(null)} />
      <AwardingConfirmDialog confirm={nfaBuildConfirm} onClose={() => setNfaBuildConfirm(null)} />
      <NfaBuildResultsDialog
        open={nfaBuildDialogOpen}
        status={nfaBuild.status}
        loading={nfaBuild.loading}
        starting={nfaBuild.starting}
        onRerun={handleBuildNfas}
        onClose={() => setNfaBuildDialogOpen(false)}
      />
      <AwardingConfirmDialog
        confirm={disableConfirm?.confirm ?? null}
        onClose={() => {
          disableConfirm?.onClose();
          setDisableConfirm(null);
        }}
      />
    </>
  );
};
